/**
 * Helper function to check if a path is currently active
 * Handles exact matches, nested routes, and dynamic segments
 *
 * Supports path patterns with dynamic segments:
 * - /manage/:customerId/home matches /manage/abc-123/home
 * - /train/overview matches /train/overview exactly
 *
 * @param currentPath - Current location pathname (e.g., /manage/abc-123/home)
 * @param targetPath - Path pattern to check (e.g., /manage/:customerId/home)
 * @returns true if target path is active
 */
export function isActivePath(
  currentPath: string,
  targetPath?: string
): boolean {
  if (!targetPath) return false;

  if (currentPath === targetPath) return true;

  if (targetPath.includes(':')) {
    const pattern = targetPath
      .split('/')
      .map(segment => (segment.startsWith(':') ? '[^/]+' : segment))
      .join('/');
    const regex = new RegExp(`^${pattern}(/|$)`);
    return regex.test(currentPath);
  }

  const normalized = targetPath.endsWith('/')
    ? targetPath.slice(0, -1)
    : targetPath;

  return currentPath.startsWith(normalized + '/');
}

/**
 * Resolves a path pattern by replacing dynamic segments with actual values
 *
 * @param path - Path pattern with dynamic segments (e.g., /manage/:customerId/home)
 * @param customerId - Customer ID to replace :customerId with
 * @returns Resolved path (e.g., /manage/abc-123/home)
 *
 * @example
 * resolvePath('/manage/:customerId/home', 'abc-123')
 * // Returns: '/manage/abc-123/home'
 */
export function resolvePath(path: string, customerId?: string): string {
  if (!customerId) return path;
  return path.replace(':customerId', customerId);
}
